import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Button,
  Collapse,
  Divider,
  Drawer,
  List,
  ListItem,
  Paper,
  Typography,
} from "@mui/material";
import request from "../../api/request";
import { Order, OrderHistory, OrderHistoryItem } from "../../const/model";
import "./OrderDetail.css";
import { BackendAPI } from "../../const/api";
import { OrderStatus } from "../../const/enum";
import { AuthContext } from "../../auth/authContext";
import EditModal, { EditField } from "../EditModal/EditModal";
import { QueryOptionsItem } from "../FilterPanel/FilterPanel";
import { getCustomerPriceListOptions } from "../../page/Dashboard/UploadPreview/UploadOrder";
import { useToast } from "../ToastBar";
import { Dialogs } from "../Dialogs";

const HistoryPanel = ({ history }) => {
  const [expanded, setExpanded] = useState<boolean[]>(
    history.map((h) => false),
  );

  const handleToggleExpand = (index) => {
    const newExpanded = [...expanded];
    newExpanded[index] = !expanded[index];
    setExpanded(newExpanded);
  };

  const recordList = (index, record) => {
    if (record.fields.length === 0) {
      return (
        <div>
          <span>无修改记录</span>
        </div>
      );
    }
    if (
      record.fields.filter(
        (f) =>
          f.updatedField === "id" && (!f.oldValue || f.oldValue === "None"),
      ).length > 0
    ) {
      return (
        <div>
          <span>
            <strong>新增订单</strong>
          </span>
        </div>
      );
    }
    return (
      <div>
        <span
          style={{
            color: "blue",
          }}
        >
          {expanded[index] ? "收起" : "展开"}
        </span>
        <Collapse in={expanded[index]} timeout="auto" unmountOnExit>
          <List>
            {record.fields.map((field: OrderHistoryItem) => (
              <ListItem key={field.updatedField}>
                <span>
                  将
                  <strong style={{ color: "red" }}>{field.updatedField}</strong>
                  由
                  <strong style={{ color: "red" }}>
                    {field.oldValue || "无"}
                  </strong>
                  改为了
                  <strong style={{ color: "red" }}>
                    {field.newValue || "无"}
                  </strong>
                </span>
              </ListItem>
            ))}
          </List>
        </Collapse>
      </div>
    );
  };

  return (
    <div>
      <Typography variant="h5">历史记录</Typography>
      {history.map((record, index) => (
        <div key={index}>
          <div onClick={() => handleToggleExpand(index)}>
            <div>
              <span>
                <strong>修改人:</strong> {record.updatedByName}
              </span>
            </div>
            <div>
              <span>
                <strong>修改时间:</strong>{" "}
                {new Date(record.updatedAt).toLocaleString()}
              </span>
            </div>
            {recordList(index, record)}
          </div>
          <Divider />
        </div>
      ))}
    </div>
  );
};

interface Props {
  order: Order;
  modelOptions: QueryOptionsItem[];
  onUpdate: () => void;
  onClose: (changed: boolean) => void;
}

const OrderDetail = (props: Props) => {
  const toast = useToast();
  const { order, modelOptions, onClose, onUpdate } = props;
  const [priceListOptions, setPriceListOptions] = useState<QueryOptionsItem[]>(
    [],
  );
  const [history, setHistory] = useState<OrderHistory[]>([]);
  const [open, setOpen] = useState(true);
  const [openAuditDialog, setOpenAuditDialog] = useState(false);
  const buttonRef = useRef(null);
  const [dialogMessage, setDialogMessage] = useState("");
  const handleConfirm = useRef<any>();
  const authContext = useContext(AuthContext);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editData, setEditData] = useState<any>({});

  useEffect(() => {
    if (authContext?.isBA() || authContext?.isAdmin()) {
      request.get(BackendAPI.priceList(order.customerCode)).then((res) => {
        if (!res.success) {
          return;
        }
        const data = res.data;
        setPriceListOptions(
          getCustomerPriceListOptions(order.customerCode, data.priceList),
        );
      });
    }
  }, [authContext, order.customerCode]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onClose(false);
  }, [onClose]);

  // close when press ESC
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleClose]);

  const handleAudit = () => {
    // 审核订单不用确认
    handleAuditConfirm().then();
  };

  const handleAuditConfirm = async () => {
    await request.put(BackendAPI.auditOrder(order.id), {}).then((res) => {
      if (!res.success) {
        return;
      }
      toast("审核成功");
      setOpenAuditDialog(false);
      onClose(true);
    });
  };

  const handleCancelConfirm = async () => {
    return await request
      .put(BackendAPI.cancelOrder, { order_ids: [order.id] })
      .then(() => {
        setOpenAuditDialog(false);
        onClose(true);
      });
  };

  const handleModify = () => {
    setEditData(order);
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!authContext) {
      return;
    }
    let success = true;
    if (authContext.isBA() || authContext.isAdmin()) {
      const res = await request.post(
        BackendAPI.updateOrderBilling(order.id),
        editData,
      );
      if (!res.success) {
        success = false;
      }
    }
    if (authContext.isWarehouse() || authContext.isAdmin()) {
      const res = await request.post(
        BackendAPI.updateOrderTracking(order.id),
        editData,
      );
      if (!res.success) {
        success = false;
      }
    }
    if (success) {
      toast("修改成功");
      onUpdate();
      handleCloseEdit();
    }
  };

  const handleCloseEdit = () => {
    setEditData({});
    setIsEditModalOpen(false);
  };

  const handleCancel = () => {
    setDialogMessage("是否需要取消这笔订单？");
    handleConfirm.current = handleCancelConfirm;
    setOpenAuditDialog(true);
  };

  const shipButtonDisabled = () => {
    if (!authContext) {
      return true;
    }
    if (!(authContext.isWarehouse() || authContext.isAdmin())) {
      return true;
    }
    return ![OrderStatus.ENTERED, OrderStatus.WAIT_SHIP].includes(order.status);
  };

  const handleShipConfirm = async () => {
    await request.put(BackendAPI.shipOrders, { order_ids: [order.id] }).then((res) => {
      if (!res.success) {
        return;
      }
      toast("发货成功");
      setOpenAuditDialog(false);
      onClose(true);
    });
  };

  const handleShip = () => {
    setDialogMessage("确认将该订单设置为已发货？（需已填写完整物流信息）");
    handleConfirm.current = handleShipConfirm;
    setOpenAuditDialog(true);
  };

  const updateButtonDisabled = () => {
    // BA任何时候都可以更新
    // 仓库只能在订单状态为已发货或待发货时更新
    if (!authContext) {
      return true;
    }
    if (authContext.isAdmin() || authContext.isBA()) {
      return false;
    }
    return !(
      authContext.isWarehouse() &&
      [
        OrderStatus.SHIPPED,
        OrderStatus.WAIT_SHIP,
        OrderStatus.ENTERED,
      ].includes(order.status)
    );
  };

  const cancelButtonDisabled = () => {
    // 客户：只有已下单和已审核才能取消
    // BA： 可以取消，但是如果已经进入发货流程，会改为拦截状态
    // 管理员：都可以取消，但是如果已经进入发货流程，会改为拦截状态；如果拦截状态，会改为已取消状态
    // 仓库： 只能取消拦截状态，表示仓库已确认未发货
    if (!authContext) {
      return true;
    }
    if (authContext.isBA()) {
      return [OrderStatus.CANCELLED, OrderStatus.WAIT_SHIP].includes(
        order.status,
      );
    }
    if (authContext.isCustomer() || authContext.isSalesScopeRole()) {
      return ![OrderStatus.PLACED, OrderStatus.REVIEWED].includes(order.status);
    }
    if (authContext.isWarehouse()) {
      return order.status !== OrderStatus.WAIT_SHIP;
    }
    return true;
  };

  const fields = () => {
    const editLabels: EditField[] = [];
    if (authContext?.isBA() || authContext?.isAdmin()) {
      editLabels.push(
        {
          label: "限价表",
          type: "select",
          options: priceListOptions,
          value: editData.priceListName,
          onChange: (data) => {
            setEditData({
              ...editData,
              priceListCode: data.code,
              priceListName: data.name,
            });
          },
        },
        {
          label: "物料",
          type: "select",
          options: modelOptions,
          value: `${editData.materialName}-${editData.materialModel}`,
          onChange: (data) => {
            setEditData({
              ...editData,
              materialId: data.id,
              materialName: data.name,
              materialModel: data.model,
            });
          },
        },
        {
          label: "收件人",
          type: "text" as const,
          value: editData.billingName,
          onChange: (value) => {
            setEditData({ ...editData, billingName: value });
          },
        },
        {
          label: "手机号",
          type: "text" as const,
          value: editData.billingTel,
          onChange: (value) => {
            setEditData({ ...editData, billingTel: value });
          },
        },
        {
          label: "收件地址",
          type: "text" as const,
          value: editData.billingAddress,
          onChange: (value) => {
            setEditData({ ...editData, billingAddress: value });
          },
        },
        {
          label: "数量",
          type: "number" as const,
          value: editData.quantity,
          onChange: (value) => {
            setEditData({ ...editData, quantity: value });
          },
        },
        {
          label: "单价",
          type: "number" as const,
          value: editData.unitPrice,
          onChange: (value) => {
            setEditData({ ...editData, unitPrice: value });
          },
        },
        {
          label: "备注",
          type: "text" as const,
          value: editData.note,
          onChange: (value) => {
            setEditData({ ...editData, note: value });
          },
        },
        {
          label: "原始订单号",
          type: "text" as const,
          value: editData.externalId,
          onChange: (value) => {
            setEditData({ ...editData, externalId: value });
          },
        },
      );
    }
    if (authContext?.isWarehouse() || authContext?.isAdmin()) {
      editLabels.push(
        {
          label: "物流",
          type: "text" as const,
          value: editData.carrier,
          onChange: (value) => {
            setEditData({ ...editData, carrier: value });
          },
        },
        {
          label: "物流单号",
          type: "text" as const,
          value: editData.trackingNumber,
          onChange: (value) => {
            setEditData({ ...editData, trackingNumber: value });
          },
        },
        {
          label: "发货地址",
          type: "text" as const,
          value: editData.shippingAddress,
          onChange: (value) => {
            setEditData({ ...editData, shippingAddress: value });
          },
        },
      );
    }
    return editLabels;
  };

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const res = await request.get(BackendAPI.orderHistory(order.id));
        if (!res.success) {
          return;
        }
        setHistory(res.data);
      } catch (error) {
        console.error("Error fetching order details:", error);
      }
    };

    fetchOrderDetails().then();
  }, [order]);

  return (
    <div>
      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        variant="persistent"
        PaperProps={{
          sx: {
            width: 400,
            height: "90%",
            maxWidth: "90%",
            position: "fixed",
            top: 80,
            right: 0,
            zIndex: 10,
            backgroundColor: "#eee",
            borderRadius: 4,
            border: "5px solid #bbb",
          },
        }}
        className="order-detail"
      >
        <Button onClick={handleClose}>关闭</Button>
        <Paper className="order-info">
          <Typography variant="h5">订单详情</Typography>
          <Typography variant="body1">
            <strong>订单号:</strong> {order.id}
          </Typography>
          <Typography variant="body1">
            <strong>原始订单号:</strong> {order.externalId}
          </Typography>
          <Typography variant="body1">
            <strong>客户名:</strong> {order.customerName}
          </Typography>
          <Typography variant="body1">
            <strong>物料名称:</strong> {order.materialName}
          </Typography>
          <Typography variant="body1">
            <strong>物料型号:</strong> {order.materialModel}
          </Typography>
          <Typography variant="body1">
            <strong>状态:</strong> {order.status}
          </Typography>
          <Typography variant="body1">
            <strong>备注:</strong> {order.note}
          </Typography>
          <Typography variant="body1">
            <strong>物流:</strong> {order.carrier}
          </Typography>
          <Typography variant="body1">
            <strong>物流单号:</strong> {order.trackingNumber}
          </Typography>
          <Typography variant="body1">
            <strong>数量:</strong> {order.quantity}
          </Typography>
          <Typography variant="body1">
            <strong>单价:</strong> {order.unitPrice}
          </Typography>
          <Typography variant="body1">
            <strong>金额:</strong> {order.totalAmount}
          </Typography>
          <Typography variant="body1">
            <strong>限价表:</strong> {order.priceListName}
          </Typography>
          <Typography variant="body1">
            <strong>收件人:</strong> {order.billingName}
          </Typography>
          <Typography variant="body1">
            <strong>手机号:</strong> {order.billingTel}
          </Typography>
          <Typography variant="body1">
            <strong>收件地址:</strong> {order.billingAddress}
          </Typography>
          <Typography variant="body1">
            <strong>创建时间:</strong> {order.createdAt}
          </Typography>
          <Divider />
          {authContext && (authContext.isBA() || authContext.isAdmin()) && (
            <Button
              onClick={handleAudit}
              ref={buttonRef}
              disabled={order.status !== OrderStatus.PLACED}
            >
              核实订单
            </Button>
          )}
          <Button onClick={handleModify} disabled={updateButtonDisabled()}>
            修改订单
          </Button>
          {(authContext?.isWarehouse() || authContext?.isAdmin()) && (
            <Button onClick={handleShip} disabled={shipButtonDisabled()}>
              发货
            </Button>
          )}
          <Button
            onClick={handleCancel}
            ref={buttonRef}
            disabled={cancelButtonDisabled()}
          >
            取消订单
          </Button>
          <Dialogs
            open={openAuditDialog}
            title={dialogMessage}
            content=""
            onConfirm={handleConfirm.current}
            onCancel={() => setOpenAuditDialog(false)}
          />
          <Divider />
          <HistoryPanel history={history} />
          <Button onClick={handleClose}>关闭</Button>
        </Paper>
      </Drawer>
      {isEditModalOpen && (
        <EditModal
          fields={fields()}
          onSave={handleSaveEdit}
          onClose={handleCloseEdit}
        />
      )}
    </div>
  );
};

export default OrderDetail;
